import React, { useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Tilt from "react-parallax-tilt";
import { Link } from "react-router-dom";
import SocialMedia from "../SocialMedia/SocialMedia";
import InquirySelect from "./InquirySelect";
import laundryLine from "../../Assets/laundry-line.webp";
import './Contact.scss';
import Seo, { breadcrumbSchema, organizationSchema, webPageSchema } from "../Seo/Seo";
import {
  CONTACT_FORM_EMAIL,
  CONTACT_FORM_SUCCESS_MESSAGE,
  submitContactForm,
} from "../../utils/contactFormSubmission";
import {
  PRODUCT_INQUIRY_FIELD_NAMES,
  appendProductInquiryMetadata,
  estimatedOrderQuantityOptions,
  needOptions,
  packagingTypeOptions,
} from "../../utils/laundrySheetInquiryFields";

function ContactForm() {
  const [status, setStatus] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderQuantity, setOrderQuantity] = useState("Not sure yet");
  const [packagingType, setPackagingType] = useState("Need Recommendation");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    setStatus(null);
    setEmailError("");

    const formData = new FormData(event.target);
    const email = formData.get("email");

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    appendProductInquiryMetadata(formData);
    formData.append("from_name", "Klean Leaf Website");
    formData.append("subject", "Klean Leaf - New Product Inquiry");

    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setStatus({ type: "success" });
      event.target.reset();
      setOrderQuantity("Not sure yet");
      setPackagingType("Need Recommendation");
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setStatus({ type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="contact-section primary page-top-settings">
      <Seo
        title="Contact Klean Leaf | Product Quotes & Inquiries"
        description="Contact Klean Leaf to discuss laundry and home care products, specifications, sample availability, packaging, and your private label project."
        path="/contact"
        schema={[
          organizationSchema,
          webPageSchema({
            name: "Contact Klean Leaf | Product Quotes & Inquiries",
            description: "Contact Klean Leaf to discuss laundry and home care products, specifications, sample availability, packaging, and your private label project.",
            path: "/contact",
            type: "ContactPage",
            primaryImage: laundryLine,
          }),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      <Container>
        <Row>
          <h1 className="center-text">Request a Product Quote</h1>
          <h2 className="page-title">
            Start a Private Label Project
          </h2>
          <p className="page-subtitle">Tell us about your products of interest, packaging needs, and project timeline.</p>
        </Row>
        <Row>
          <Col md={6} className="kitty">
            <Tilt>
              <img
                src={laundryLine}
                alt="Laundry line illustration"
                className="img-fluid"
                decoding="async"
              />
            </Tilt>
          </Col>
          <Col md={5} className="contact-right equal-height-cards">
            <aside className="contact-product-discovery" aria-labelledby="contact-products-heading">
              <h2 id="contact-products-heading">Still choosing a product?</h2>
              <p>Explore our laundry and home-care collection to find the product formats that fit your project before sending an inquiry.</p>
              <Link to="/products" className="secondary-btn">Browse Products</Link>
            </aside>
            <form onSubmit={onSubmit} className="contact-form">
              <label htmlFor="contact-name">Name *</label>
              <input id="contact-name" type="text" name="name" autoComplete="name" required />

              <label htmlFor="contact-email">Email *</label>
              <input
                id="contact-email"
                type="email"
                name="email"
                autoComplete="email"
                required
                aria-describedby={emailError ? "contact-email-error" : undefined}
                aria-invalid={emailError ? "true" : "false"}
                onChange={(e) => {
                  setEmailError(validateEmail(e.target.value) ? "" : "Please enter a valid email address.");
                }}
                onFocus={() => setEmailError("")}
              />
              {emailError && <span id="contact-email-error" className="error-message">{emailError}</span>}

              <label htmlFor="contact-company">Company</label>
              <input id="contact-company" type="text" name="company" autoComplete="organization" />

              <fieldset className="form-fieldset">
                <legend>Product / Project Details</legend>

                <label htmlFor="contact-product-name">Product of Interest</label>
                <input
                  id="contact-product-name"
                  type="text"
                  name={PRODUCT_INQUIRY_FIELD_NAMES.productName}
                  placeholder="Product name or category"
                />

                <label htmlFor="contact-order-quantity">Estimated Order Quantity</label>
                <InquirySelect
                  id="contact-order-quantity"
                  name={PRODUCT_INQUIRY_FIELD_NAMES.estimatedOrderQuantity}
                  value={orderQuantity}
                  onChange={setOrderQuantity}
                  options={estimatedOrderQuantityOptions}
                />

                <label htmlFor="contact-packaging-type">Packaging Type</label>
                <InquirySelect
                  id="contact-packaging-type"
                  name={PRODUCT_INQUIRY_FIELD_NAMES.packagingType}
                  value={packagingType}
                  onChange={setPackagingType}
                  options={packagingTypeOptions}
                />

                <label htmlFor="contact-expected-launch-date">Expected Launch Date</label>
                <input
                  id="contact-expected-launch-date"
                  type="date"
                  name={PRODUCT_INQUIRY_FIELD_NAMES.expectedLaunchDate}
                />

                <div className="checkbox-field">
                  <span className="checkbox-label" id="contact-needs-label">What Do You Need?</span>
                  <div className="checkbox-options" role="group" aria-labelledby="contact-needs-label">
                    {needOptions.map((option) => (
                      <label className="checkbox-option" key={option}>
                        <input
                          type="checkbox"
                          name={PRODUCT_INQUIRY_FIELD_NAMES.needs}
                          value={option}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </fieldset>

              <label htmlFor="contact-message">Message *</label>
              <textarea id="contact-message" name="message" autoComplete="off" required maxLength="2000"></textarea>

              <button type="submit" className="submit-button primary-btn" disabled={isSubmitting}>
                {isSubmitting ? "Sending..." : "Request Samples or Quote"}
              </button>
              {status && (
                <span className={`form-result ${status.type}`} role={status.type === "error" ? "alert" : "status"}>
                  {status.type === "success" ? (
                    CONTACT_FORM_SUCCESS_MESSAGE
                  ) : (
                    <>
                      We couldn’t send your message right now. Please try again, or email us directly at{" "}
                      <a href={`mailto:${CONTACT_FORM_EMAIL}`}>{CONTACT_FORM_EMAIL}</a>.
                    </>
                  )}
                </span>
              )}
            </form>
          </Col>
        </Row>

        <Row className="mt-5">
          <h2>Talk to Our Columbus, Ohio Team</h2>
          <p>Email <strong>hello@kleanleaf.us</strong> or use the form to discuss product specifications, sample availability, pricing, packaging options, and supply requirements.</p>
          <p>Our Office: Columbus, Ohio</p>
          <p>Helpful details to include: packaging type, estimated order quantity, desired launch date, and whether you need samples, a quote, custom formula support, or packaging support.</p>
          <p>Follow us on:</p>
          <SocialMedia />
        </Row>
      </Container>
    </Container>
  );
}

export default ContactForm;
